import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Heading,
  SimpleGrid,
  Card,
  CardBody,
  Badge,
  Text,
  VStack,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  Spinner,
  Center,
  Flex,
  Divider,
  Container,
  Button,
  useToast,
  Alert,
  AlertIcon,
  Image,
} from '@chakra-ui/react';
import { SearchIcon, RepeatIcon } from '@chakra-ui/icons';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { VIPInfo } from '../types/api';

// ============================================
// SSE Hook for VIP Board Real-Time Updates
// ============================================
const VIP_BOARD_FALLBACK_REFETCH_MS = 60_000;
const VIP_BOARD_RETRY_MIN_MS = 5_000;
const VIP_BOARD_RETRY_MAX_MS = 60_000;

const useVIPBoardStream = (uuid: string, enabled: boolean) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!enabled || !uuid) {
      console.log('VIP Board SSE disabled');
      return;
    }

    const apiUrl = process.env.REACT_APP_API_URL || '/api';

    console.log(`🔌 Establishing VIP Board SSE connection for UUID: ${uuid}`);

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let retryDelay = VIP_BOARD_RETRY_MIN_MS;

    const connect = () => {
      const eventSource = new EventSource(
        `${apiUrl}/checkin/vip-board/${uuid}/stream`
      );

      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        console.log('✅ VIP Board SSE connection established');
        setIsConnected(true);
        retryDelay = VIP_BOARD_RETRY_MIN_MS;
        // Anything broadcast while we were disconnected is lost: resync the full list on every (re)connect.
        queryClient.refetchQueries({ queryKey: ['vip-board', uuid], type: 'active' });
      };

      eventSource.addEventListener('connected', (event) => {
        console.log('📡 VIP Board SSE connected:', event.data);
      });

      eventSource.addEventListener('update', async (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📡 VIP Board update:', data);

          setLastUpdate(new Date());
        
          // ✅ Use refetchQueries for immediate update
          await queryClient.refetchQueries({ 
            queryKey: ['vip-board', uuid],
            type: 'active'
          });

          const action = data.type === 'cancel' ? 'Check-in annulé' : 'Nouveau check-in';

          toast({
            title: `${data.contact_name}`,
            description: action,
            status: data.type === 'cancel' ? 'info' : 'success',
            duration: 3000,
            position: 'top-right',
            isClosable: true,
          });

        } catch (error) {
          console.error('❌ Error processing VIP Board SSE update:', error);
        }
      });

      eventSource.addEventListener('heartbeat', (event) => {
        console.log('💓 VIP Board heartbeat');
      });

      eventSource.onerror = (error) => {
        console.error('❌ VIP Board SSE error:', error);
        setIsConnected(false);
        // After a non-200 response (502/504 during a deploy) the browser gives up: reopen ourselves.
        if (eventSource.readyState === EventSource.CLOSED && !cancelled) {
          eventSource.close();
          if (eventSourceRef.current === eventSource) eventSourceRef.current = null;
          retryTimer = setTimeout(connect, retryDelay);
          retryDelay = Math.min(retryDelay * 2, VIP_BOARD_RETRY_MAX_MS);
        }
      };
    };

    connect();

    // Safety net: periodic refetch while the page is open, whatever the SSE state.
    const fallbackTimer = setInterval(() => {
      queryClient.refetchQueries({ queryKey: ['vip-board', uuid], type: 'active' });
    }, VIP_BOARD_FALLBACK_REFETCH_MS);

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      clearInterval(fallbackTimer);
      console.log('🔌 Closing VIP Board SSE connection');
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setIsConnected(false);
    };
  }, [uuid, enabled, queryClient, toast]);

  return { isConnected, lastUpdate };
};

// ============================================
// VIP Board Page Component
// ============================================
const VIPBoardPage: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const [searchQuery, setSearchQuery] = useState('');
  const toast = useToast();

  // Enable real-time updates
  const { isConnected, lastUpdate } = useVIPBoardStream(uuid!, !!uuid);

  // Fetch VIP board data
  const { data: vipBoard, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['vip-board', uuid],
    queryFn: () => apiService.getVIPBoard(uuid!),
    enabled: !!uuid,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  // Search results
  const { data: searchResults } = useQuery({
    queryKey: ['vip-search', uuid, searchQuery],
    queryFn: () => apiService.searchVIPBoard(uuid!, searchQuery),
    enabled: !!uuid && searchQuery.length >= 2,
  });

  const handleRefresh = async () => {
    await refetch();
    toast({
      title: 'Mise à jour effectuée',
      status: 'success',
      duration: 2000,
      position: 'top',
    });
  };

  const getVIPBadgeColor = (status: string) => {
    switch (status) {
      case 'very_vip': return 'purple';
      case 'board_member': return 'red';
      case 'vip': return 'blue';
      case 'sponsor': return 'green';
      case 'media': return 'orange';
      default: return 'gray';
    }
  };

  const getVIPLabel = (status: string) => {
    switch (status) {
      case 'very_vip': return 'VIP+';
      case 'board_member': return 'Conseil';
      case 'vip': return 'VIP';
      case 'sponsor': return 'Sponsor';
      case 'media': return 'Média';
      case 'none': return 'Invité';
      default: return status;
    }
  };

  if (isLoading) {
    return (
      <Center minH="100vh" bg="gray.50">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  if (!vipBoard) {
    return (
      <Center minH="100vh" bg="gray.50">
        <Text color="red.500" fontSize="xl">
          Tableau VIP non trouvé
        </Text>
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" py={{ base: 4, md: 6 }}>
      <Container maxW="container.xl">
        <VStack spacing={{ base: 4, md: 6 }} align="stretch">
          {/* Logo Header */}
          <Card overflow="hidden" p={0} shadow="sm">
            <Box w="full">
              <Image
                src="/logo_events.svg"
                alt="FFM Events"
                w="100%"
                h="auto"
                display="block"
              />
            </Box>
          </Card>

          {/* Event Title & Refresh */}
          <Flex
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align={{ base: 'stretch', md: 'center' }}
            gap={3}
          >
            <Heading size={{ base: 'md', md: 'lg' }} color="blue.900">
              {vipBoard.event.title}
            </Heading>

            <Button
              leftIcon={<RepeatIcon />}
              colorScheme="blue"
              onClick={handleRefresh}
              isLoading={isFetching}
              size={{ base: 'sm', md: 'md' }}
            >
              Rafraîchir
            </Button>
          </Flex>

          {/* Connection Status - Compact */}
          {!isConnected && (
            <Alert status="error" size="sm" borderRadius="md" data-testid="vip-board-disconnected">
              <AlertIcon boxSize="16px" />
              <Text fontSize="xs" fontWeight="medium">déconnecté — reconnexion…</Text>
            </Alert>
          )}
          {isConnected && (
            <Alert status="success" size="sm" borderRadius="md">
              <AlertIcon boxSize="16px" />
              <HStack spacing={2} fontSize="xs">
                <Text fontWeight="medium">connecté au check-in</Text>
                {lastUpdate && (
                  <Text color="gray.600">
                    • dernière MAJ {lastUpdate.toLocaleTimeString('fr-FR')}
                  </Text>
                )}
              </HStack>
            </Alert>
          )}

          {/* Stats - 2 rows, sober & structured */}
          <Card bg="blue.900" color="white" shadow="lg">
            <CardBody py={5}>
              <VStack align="stretch" spacing={4}>
                {/* Row 1: Global guests */}
                <HStack spacing={4} justify="space-between" align="stretch">
                  <Box flex={1}>
                    <Text
                      fontSize="xs"
                      textTransform="uppercase"
                      letterSpacing="0.08em"
                      color="blue.100"
                    >
                      Total attendus
                    </Text>
                    <HStack align="baseline" spacing={2} mt={1}>
                      <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="semibold">
                        {vipBoard.stats.total_accepted || 0}
                      </Text>
                    </HStack>
                  </Box>

                  <Box
                    w="1px"
                    alignSelf="stretch"
                    bg="blue.700"
                    display={{ base: 'none', md: 'block' }}
                  />

                  <Box flex={1}>
                    <Text
                      fontSize="xs"
                      textTransform="uppercase"
                      letterSpacing="0.08em"
                      color="blue.100"
                    >
                      Total présents
                    </Text>
                    <HStack align="baseline" spacing={2} mt={1}>
                      <Text
                        fontSize={{ base: '2xl', md: '3xl' }}
                        fontWeight="semibold"
                        color="green.300"
                      >
                        {vipBoard.stats.checked_in_total || 0}
                      </Text>
                      <Text fontSize="sm" color="blue.200">
                        {vipBoard.stats.total_accepted > 0
                          ? `${Math.round(
                              (vipBoard.stats.checked_in_total / vipBoard.stats.total_accepted) * 100
                            )}%`
                          : '0%'}
                      </Text>
                    </HStack>
                  </Box>
                </HStack>

                <Divider borderColor="blue.700" />

                {/* Row 2: VIPs only */}
                <HStack spacing={4} justify="space-between" align="stretch">
                  <Box flex={1}>
                    <Text
                      fontSize="xs"
                      textTransform="uppercase"
                      letterSpacing="0.08em"
                      color="blue.100"
                    >
                      VIPs attendus
                    </Text>
                    <HStack align="baseline" spacing={2} mt={1}>
                      <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="semibold">
                        {vipBoard.stats.total_vips || 0}
                      </Text>
                    </HStack>
                  </Box>

                  <Box
                    w="1px"
                    alignSelf="stretch"
                    bg="blue.700"
                    display={{ base: 'none', md: 'block' }}
                  />

                  <Box flex={1}>
                    <Text
                      fontSize="xs"
                      textTransform="uppercase"
                      letterSpacing="0.08em"
                      color="blue.100"
                    >
                      VIPs présents
                    </Text>
                    <HStack align="baseline" spacing={2} mt={1}>
                      <Text
                        fontSize={{ base: '2xl', md: '3xl' }}
                        fontWeight="semibold"
                        color="green.300"
                      >
                        {vipBoard.stats.checked_in || 0}
                      </Text>
                      <Text fontSize="sm" color="blue.200">
                        {vipBoard.stats.total_vips > 0
                          ? `${Math.round(
                              (vipBoard.stats.checked_in / vipBoard.stats.total_vips) * 100
                            )}%`
                          : '0%'}
                      </Text>
                    </HStack>
                  </Box>
                </HStack>
              </VStack>
            </CardBody>
          </Card>

          {/* Search */}
          <Card shadow="sm">
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <InputGroup size={{ base: 'md', md: 'lg' }}>
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Rechercher un invité (VIP ou non)..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    fontSize={{ base: 'md', md: 'lg' }}
                    autoComplete="off"
                    borderColor="gray.300"
                    _hover={{ borderColor: 'blue.400' }}
                    _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
                  />
                </InputGroup>

                {/* Search Results */}
                {searchQuery.length >= 2 && searchResults && (
                  <VStack align="stretch" spacing={2}>
                    <Text fontWeight="bold" fontSize="sm" color="gray.600">
                      Résultats ({searchResults.results.length})
                    </Text>
                    {searchResults.results.length === 0 ? (
                      <Text color="gray.500" fontSize="sm">
                        Aucun résultat
                      </Text>
                    ) : (
                      searchResults.results.map((result, idx) => (
                        <Card
                          key={idx}
                          bg={result.checked_in ? 'green.50' : 'white'}
                          size="sm"
                          borderColor={result.checked_in ? 'green.300' : 'gray.200'}
                          borderWidth="1px"
                        >
                          <CardBody>
                            <HStack justify="space-between">
                              <VStack align="start" spacing={0}>
                                <Text fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>
                                  {result.name}
                                </Text>
                                <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                                  {result.organization}
                                </Text>
                              </VStack>
                              <HStack>
                                {result.vip_status !== 'none' && (
                                  <Badge
                                    colorScheme={getVIPBadgeColor(result.vip_status)}
                                    fontSize={{ base: 'xs', md: 'sm' }}
                                  >
                                    {getVIPLabel(result.vip_status)}
                                  </Badge>
                                )}
                                {result.checked_in ? (
                                  <Badge colorScheme="green" fontSize={{ base: 'xs', md: 'md' }}>
                                    ✓ Présent
                                  </Badge>
                                ) : (
                                  <Badge colorScheme="orange" fontSize={{ base: 'xs', md: 'sm' }}>
                                    En attente
                                  </Badge>
                                )}
                              </HStack>
                            </HStack>
                          </CardBody>
                        </Card>
                      ))
                    )}
                  </VStack>
                )}
              </VStack>
            </CardBody>
          </Card>

          {/* VIP List */}
          <VStack spacing={4} align="stretch">
            <Flex justify="space-between" align="center">
              <Heading size={{ base: 'sm', md: 'md' }} color="blue.900">
                Liste des VIPs
              </Heading>
              <Badge colorScheme="blue" fontSize={{ base: 'xs', md: 'sm' }} px={3} py={1}>
                {vipBoard.vips.length} VIP{vipBoard.vips.length > 1 ? 's' : ''}
              </Badge>
            </Flex>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
              {vipBoard.vips.map((vip: VIPInfo, idx: number) => (
                <Card
                  key={idx}
                  bg={vip.checked_in ? 'green.50' : 'gray.100'}
                  shadow="sm"
                  borderWidth="2px"
                  borderColor={vip.checked_in ? 'green.400' : 'gray.300'}
                  _hover={{ shadow: 'md' }}
                  transition="all 0.2s"
                >
                  <CardBody p={3}>
                    <VStack align="start" spacing={2}>
                      <Flex justify="space-between" w="full" align="start">
                        <VStack align="start" spacing={0.5} flex={1}>
                          <Text 
                            fontWeight="bold" 
                            fontSize={{ base: 'md', md: 'lg' }} 
                            color={vip.checked_in ? 'green.800' : 'gray.700'}
                            lineHeight="shorter"
                          >
                            {vip.name}
                          </Text>
                          {vip.official_title && (
                            <Text
                              fontSize={{ base: 'xs', md: 'sm' }}
                              color={vip.checked_in ? 'gray.600' : 'gray.500'}
                              fontStyle="italic"
                              lineHeight="shorter"
                            >
                              {vip.official_title}
                            </Text>
                          )}
                        </VStack>
                        {vip.checked_in ? (
                          <Badge 
                            colorScheme="green" 
                            fontSize={{ base: 'xs', md: 'sm' }} 
                            px={2} 
                            py={0.5}
                            flexShrink={0}
                          >
                            Présent
                          </Badge>
                        ) : (
                          <Badge 
                            colorScheme="gray" 
                            fontSize={{ base: 'xs', md: 'sm' }} 
                            px={2} 
                            py={0.5}
                            flexShrink={0}
                          >
                            En attente
                          </Badge>
                        )}
                      </Flex>

                      {vip.organization && (
                        <Text 
                          fontSize={{ base: 'xs', md: 'sm' }} 
                          color={vip.checked_in ? 'gray.700' : 'gray.600'}
                        >
                          {vip.organization}
                        </Text>
                      )}

                      <HStack spacing={2} wrap="wrap">
                        <Badge
                          colorScheme={getVIPBadgeColor(vip.vip_status)}
                          fontSize={{ base: 'xs', md: 'sm' }}
                        >
                          {getVIPLabel(vip.vip_status)}
                        </Badge>
                        {vip.checked_in && vip.checked_in_at && (
                          <Text fontSize="xs" color="gray.600"> check-in
                            à {new Date(vip.checked_in_at).toLocaleTimeString('fr-FR', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </Text>
                        )}
                      </HStack>
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>

            {vipBoard.vips.length === 0 && (
              <Card>
                <CardBody>
                  <Center py={8}>
                    <Text color="gray.500">
                      Aucun VIP pour cet événement
                    </Text>
                  </Center>
                </CardBody>
              </Card>
            )}
          </VStack>


        {/* Discrete Footer */}
        <Box 
          py={6} 
          textAlign="center"
          borderTop="1px solid"
          borderColor="gray.200"
          mt={8}
        >
          <Text fontSize="xs" color="gray.400">
            Réalisation{' '}
            <Box
              as="a"
              href="https://themoto.company"
              target="_blank"
              rel="noopener noreferrer"
              color="gray.400"
              _hover={{ 
                color: "gray.400",
                textDecoration: "underline" 
              }}
              transition="color 0.2s"
            >
              The Moto Company
            </Box>
          </Text>
        </Box>



        </VStack>
      </Container>
    </Box>
  );
};

export default VIPBoardPage;